// hook รวมของหน้า Setting — แยกจากไฟล์เรียก API เพื่อให้ component ไม่ต้องรู้ queryKey
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brandKeys, fetchBrands } from "@features/brands/api/brands-api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import {
  type AdPlatform,
  fetchBiddableAccounts,
  fetchBiddableProjects,
  fetchGroups,
  fetchKolCampaign,
  fetchKolCampaigns,
  fetchPickablePages,
  fetchProducts,
  settingsKeys,
} from "./settings-api";

/** แคตตาล็อกสินค้า ~700 รายการ — โหลดครั้งเดียวต่อ session แล้วค้นฝั่ง client */
export function useProducts() {
  return useQuery({
    queryKey: settingsKeys.products,
    queryFn: fetchProducts,
    staleTime: 30 * 60_000,
  });
}

export function useGroups() {
  return useQuery({ queryKey: settingsKeys.groups, queryFn: fetchGroups });
}

/** แบรนด์ที่ enable แล้ว — ใช้เป็น Map เพื่อเช็คว่า id นี้อยู่ใน My Job หรือยัง */
export function useEnabledBrands() {
  const q = useQuery({ queryKey: brandKeys.all, queryFn: fetchBrands });
  return { ...q, byId: new Map((q.data ?? []).map((b) => [b.id, b])) };
}

export function usePickablePages() {
  return useQuery({
    queryKey: settingsKeys.inboxPages,
    queryFn: fetchPickablePages,
    enabled: !!wazzupToken(),
    retry: false,
    staleTime: 10 * 60_000,
  });
}

export function useKolCampaigns() {
  return useQuery({
    queryKey: settingsKeys.kolCampaigns,
    queryFn: fetchKolCampaigns,
    enabled: !!wazzupToken(),
    retry: false,
    staleTime: 10 * 60_000,
  });
}

/**
 * ชื่อแคมเปญ KOL ตัวเดียว — เปิดใช้เฉพาะตอนที่ยังไม่รู้ชื่อจากที่อื่น
 * แคชยาวเพราะชื่อแคมเปญแทบไม่เปลี่ยน และหน้านี้เรียกทีละหลายรหัสพร้อมกัน
 */
export function useKolCampaign(key: string, enabled: boolean) {
  return useQuery({
    queryKey: settingsKeys.kolCampaign(key),
    queryFn: () => fetchKolCampaign(key),
    enabled: enabled && !!key,
    retry: false,
    staleTime: 30 * 60_000,
  });
}

/** รายชื่อ project ฝั่ง Biddable — โหลดครั้งเดียวต่อ session ของหน้าตั้งค่า */
export function useBiddableProjects() {
  return useQuery({
    queryKey: settingsKeys.biddableProjects,
    queryFn: fetchBiddableProjects,
    enabled: !!wazzupToken(),
    retry: false,
    staleTime: 10 * 60_000,
  });
}

export function useBiddableAccounts(connector: AdPlatform) {
  return useQuery({
    queryKey: settingsKeys.biddableScope(connector),
    queryFn: () => fetchBiddableAccounts(connector),
    enabled: !!wazzupToken(),
    retry: false,
    staleTime: 10 * 60_000,
  });
}

/** ใช้หลังบันทึกอะไรที่กระทบรายการแบรนด์/กลุ่ม เพื่อให้ทุกหน้าเห็นค่าล่าสุด */
export function useInvalidateSettings() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: brandKeys.all });
    void qc.invalidateQueries({ queryKey: settingsKeys.groups });
  };
}

export { useMutation };
